import { FaWhatsapp } from "react-icons/fa6";
import { FaPhoneAlt } from "react-icons/fa";
import styles from "./seller-contact-dialog.module.css";
import { MdChat, MdClose } from "react-icons/md";
import {
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "@/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { CircleFlag } from "react-circle-flags";
import { IoIosArrowDown } from "react-icons/io";
import {
  getCountryFromPrefix,
  phonePrefixes,
} from "@/lib/phone-prefixes/prefixes";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import { ContactDialogType, UserTypes } from "@/types/types";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { tourContactHandler } from "./handlers";
import { toast } from "react-toastify";
import { tourContactSchema, tourContactSchemaAuth } from "./schemas";
import { env } from "@/lib/env";

const PROFILES_ENDPOINT_URL = env.NEXT_PROFILES_API_URL;

export default function TourContactDialog({
  userSub,
  photoUrl,
  userName,
  userType,
  closeCallback,
  phoneNumber,
  hasWhatsAppActive,
  dialogRef,
  listingId,
  listingTitle,
  activeType,
  setActiveType,
  isComponent = false,
}: {
  userSub: string;
  photoUrl: string;
  userName: string;
  userType: UserTypes;
  closeCallback: () => void;
  phoneNumber: string | null;
  hasWhatsAppActive: boolean | null;
  dialogRef: RefObject<HTMLDivElement | null>;
  listingId: string;
  listingTitle: string;
  activeType: ContactDialogType;
  setActiveType: (newType: ContactDialogType) => void;
  isComponent?: boolean;
}) {
  const t = useTranslations("ContactDialog.tour");
  const [isPhoneNumberActive, setIsPhoneNumberActive] = useState(false);
  const [isPhonePrefixOpen, setIsPhonePrefixOpen] = useState(false);
  const locale = useLocale();
  const [numberSearchQuery, setNumberSearchQuery] = useState("");
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });
  const filteredPrefixes = phonePrefixes.filter((country) =>
    (displayNames.of(country.code) ?? "")
      .toLowerCase()
      .includes(numberSearchQuery.toLowerCase()),
  );
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: "long" });
  const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: "long" });

  const firstName = userName.split(" ")[0] ?? "";
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [activePrefix, setActivePrefix] = useState("");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const { userData } = useUserAuthDataContext();

  const scrollRef = useRef<HTMLUListElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const wasDragging = useRef(false);

  useEffect(() => {
    const slider = scrollRef.current;
    if (!slider) return;

    const handlePointerDown = (e: PointerEvent) => {
      isDraggingRef.current = true;
      slider.style.cursor = "grabbing";
      startXRef.current = e.pageX - slider.offsetLeft;
      scrollLeftRef.current = slider.scrollLeft;
      wasDragging.current = false;
      e.stopPropagation();
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDraggingRef.current) return;

      e.stopPropagation();
      e.preventDefault(); // ensure smooth drag on touch devices

      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startXRef.current) * 1.3;

      if (Math.abs(walk) > 5) {
        wasDragging.current = true;
      }

      slider.scrollLeft = scrollLeftRef.current - walk;
    };

    const handlePointerUp = (e: PointerEvent) => {
      e.stopPropagation();
      isDraggingRef.current = false;
      slider.style.cursor = "";
      slider.releasePointerCapture(e.pointerId);
    };

    const onClick = (e: MouseEvent) => {
      if (wasDragging.current) {
        e.preventDefault();
        e.stopPropagation(); // Cancel the click if dragged
      }
    };

    const endDrag = (e: PointerEvent) => {
      isDraggingRef.current = false;
      try {
        slider.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Avoid crashing if pointer wasn't captured
      }
    };

    slider.addEventListener("pointerdown", handlePointerDown);
    slider.addEventListener("pointerup", handlePointerUp);
    slider.addEventListener("pointermove", handlePointerMove);
    slider.addEventListener("pointerup", endDrag);
    slider.addEventListener("pointerleave", endDrag);
    slider.addEventListener("click", onClick, true); // capture phase

    return () => {
      slider.removeEventListener("pointerdown", handlePointerDown);
      slider.removeEventListener("pointerup", handlePointerUp);
      slider.removeEventListener("pointermove", handlePointerMove);
      slider.removeEventListener("pointerup", endDrag);
      slider.removeEventListener("pointerleave", endDrag);
      slider.removeEventListener("click", onClick, true); // capture phase
    };
  }, []);

  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [isDaysLoading, setIsDaysLoading] = useState(true);

  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);

  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  useEffect(() => {
    if (userType === UserTypes.PRIVATEUSER) {
      const dates = getNextDaysUTC(14);
      setAvailableDays(dates);
      setIsDaysLoading(false);
      return;
    }
    console.log(userType);
    const fetchDays = async () => {
      try {
        setIsDaysLoading(true);

        const res = await fetch(
          `${PROFILES_ENDPOINT_URL}/getAgentAvailability`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              origin: "http://doomum.ro:3000/",
            },
            body: JSON.stringify({
              agent_id: userSub,
              mode: "days",
            }),
          },
        );
        console.log(res);
        if (!res.ok) throw new Error();

        const data = await res.json();
        console.log(data);

        setAvailableDays(data.days || []);
      } catch {
        toast("Unable to load available dates.", { type: "error" });
      } finally {
        setIsDaysLoading(false);
      }
    };

    fetchDays();
  }, [userSub, userType]);

  useEffect(() => {
    if (userType === UserTypes.PRIVATEUSER) return;
    if (!selectedDate) return;

    const fetchSlots = async () => {
      try {
        setIsSlotsLoading(true);
        setAvailableSlots([]);
        setSelectedSlot(null);

        const res = await fetch(
          `${PROFILES_ENDPOINT_URL}/getAgentAvailability`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              origin: "http://doomum.ro:3000/",
            },
            body: JSON.stringify({
              agent_id: userSub,
              mode: "slots",
              selected_day_utc: selectedDate,
            }),
          },
        );

        if (!res.ok) throw new Error();

        const data = await res.json();

        setAvailableSlots(data.slots || []);
      } catch {
        toast("Unable to load available time slots.", { type: "error" });
      } finally {
        setIsSlotsLoading(false);
      }
    };

    fetchSlots();
  }, [selectedDate, userSub, userType]);

  useEffect(() => {
    if (userType === UserTypes.PRIVATEUSER) {
      if (!selectedDate) return;

      const prettyDate = new Date(selectedDate).toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      setMessage(
        t("placeholderMessage", {
          userName: firstName,
          date: prettyDate,
        }),
      );
      return;
    }

    // agents → use slot
    if (selectedSlot) {
      const prettyDate = new Date(selectedSlot).toLocaleString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      setMessage(
        t("placeholderMessage", {
          userName: firstName,
          date: prettyDate,
        }),
      );
    }
  }, [selectedDate, selectedSlot, userType, locale, t, firstName]);

  return (
    <div
      className={`${styles.dialogWrapper} ${
        isComponent ? styles.isComponent : ""
      }`}
      onClick={(e) => {
        if (!isComponent) {
          e.stopPropagation();
          closeCallback();
        }
      }}
    >
      <div
        className={styles.dialogContent}
        ref={dialogRef}
        onClick={(e) => !isComponent && e.stopPropagation()}
      >
        <div className={styles.closeButtonWrapper}>
          <span className={styles.title}>{t("titleMessage")}</span>
          <button
            className={styles.closeButton}
            onClick={(e) => {
              e.preventDefault();
              closeCallback();
            }}
          >
            <MdClose className={styles.closeIcon} />
          </button>
        </div>
        <div className={styles.agentDetails}>
          <Link
            className={`${styles.imageLink} ${styles.imageLinkDesktop}`}
            target="_blank"
            rel="noopener noreferrer"
            href={makeLocalizedHrefNoLocale(locale, "profiles", [`${userSub}`])}
          >
            <Image
              className={styles.agentImage}
              src={photoUrl}
              width={128}
              height={128}
              alt="User uploaded profile photo"
            />
          </Link>
          <p className={styles.userName}>{userName}</p>
        </div>
        <div className={styles.mainContentWrapper}>
          <div className={styles.selector}>
            <button
              onClick={(e) => {
                e.preventDefault();
                setActiveType(ContactDialogType.LISTING);
              }}
              className={`${
                activeType === ContactDialogType.LISTING && styles.isActive
              }`}
            >
              {t("message")}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                setActiveType(ContactDialogType.TOUR);
              }}
              className={`${
                activeType === ContactDialogType.TOUR && styles.isActive
              }`}
            >
              {t("requestTour")}
            </button>
          </div>
          <div className={styles.datePickerWrapper}>
            <p className={styles.dateTitle}>{t("selectDate")}</p>
            <ul className={styles.dateList} ref={scrollRef}>
              {isDaysLoading && (
                <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <li key={`skeleton-day-${i}`} className={styles.dateItem}>
                      <div
                        className={`${styles.dateSkeleton} ${styles.skeleton}`}
                      />
                    </li>
                  ))}
                </>
              )}

              {!isDaysLoading && availableDays.length === 0 && (
                <p>No available dates</p>
              )}
              {availableDays.map((day, index) => {
                const d = new Date(day);
                return (
                  <li
                    key={index}
                    className={`${styles.dateItem} ${
                      selectedDate === day ? styles.selected : ""
                    }`}
                  >
                    <button
                      type="button"
                      className={styles.dateButton}
                      onClick={(e) => {
                        setSelectedDate(day);
                      }}
                      aria-pressed={selectedDate === day}
                      aria-label={`${weekdayFormatter.format(
                        d,
                      )}, ${monthFormatter.format(d)} ${d.getDate()}`}
                    >
                      <p className={styles.dateNumber}>{d.getDate()}</p>
                      <p className={styles.dateMonth}>
                        {monthFormatter.format(d)}
                      </p>
                      <p className={styles.dateWeekDay}>
                        {weekdayFormatter.format(d)}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          {userType !== UserTypes.PRIVATEUSER && (
            <div className={styles.slotsWrapper}>
              <p className={styles.dateTitle}>{t("selectTime")}</p>

              {!isSlotsLoading &&
                selectedDate &&
                availableSlots.length === 0 && <p>No available times</p>}

              <div className={styles.slotsGrid}>
                {isSlotsLoading && (
                  <>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <div
                        key={`skeleton-slot-${i}`}
                        className={`${styles.slotSkeleton} ${styles.skeleton}`}
                      />
                    ))}
                  </>
                )}
                {availableSlots.map((slot, index) => {
                  const date = new Date(slot);

                  const timeLabel = date.toLocaleTimeString(locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                  });

                  return (
                    <button
                      key={index}
                      disabled={isSlotsLoading}
                      className={`${styles.slotButton} ${
                        selectedSlot === slot ? styles.selectedSlot : ""
                      }`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      {timeLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {!userData.idToken || userType === UserTypes.AGENCY ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const tourDate =
                  userType === UserTypes.PRIVATEUSER
                    ? selectedDate
                    : selectedSlot;
                const validation = tourContactSchema.safeParse({
                  fullName,
                  email,
                  phoneNumber: `${activePrefix + phoneNumberInput}`,
                  message,
                  listingId,
                  tourDate: tourDate,
                });
                if (!validation.success) {
                  const firstError = validation.error.errors[0]?.message;
                  toast(t(firstError), { type: "error" });
                  return;
                }
                if (!tourDate) {
                  toast(t("invalidTourDate"), { type: "error" });
                  return;
                }
                const isSuccess = await tourContactHandler(
                  userSub,
                  userType,
                  fullName,
                  email,
                  `${activePrefix + phoneNumberInput}`,
                  message,
                  listingId,
                  listingTitle,
                  userData.idToken,
                  userData.sub,
                  tourDate,
                );
                if (isSuccess) {
                  setActivePrefix("");
                  setEmail("");
                  setFullName("");
                  setMessage("");
                  setPhoneNumberInput("");
                  closeCallback();
                }
              }}
              className={styles.contactForm}
            >
              <span className={styles.description}>{t("contactInfo")}</span>
              <div className={`${styles.fieldWrapper}`}>
                <input
                  type="text"
                  placeholder={t("completeName")}
                  value={fullName}
                  onChange={(e) => {
                    e.preventDefault();
                    setFullName(e.target.value);
                  }}
                />
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <input
                  type="email"
                  placeholder={t("email")}
                  value={email}
                  onChange={(e) => {
                    e.preventDefault();
                    setEmail(e.target.value);
                  }}
                />
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <div className={styles.phoneNumberWrapper}>
                  <button
                    className={styles.prefixButton}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPhonePrefixOpen(!isPhonePrefixOpen);
                      setNumberSearchQuery("");
                    }}
                  >
                    {activePrefix && (
                      <span>
                        <CircleFlag
                          countryCode={getCountryFromPrefix(
                            activePrefix,
                          ).toLocaleLowerCase()}
                          className={styles.countryIcon}
                        />
                      </span>
                    )}
                    {activePrefix == "" ? t("none") : activePrefix}
                    <span>
                      <IoIosArrowDown className={styles.arrowIcon} />
                    </span>
                  </button>
                  <input
                    className={styles.phoneInput}
                    type="number"
                    placeholder="797 285 954"
                    value={phoneNumberInput}
                    onChange={(e) => {
                      e.preventDefault();
                      setPhoneNumberInput(e.target.value);
                    }}
                  />
                  {isPhonePrefixOpen && (
                    <div className={styles.phonePrefixesWrapper}>
                      <input
                        className={styles.phonePrefixInput}
                        type="text"
                        placeholder={t("phoneCountryPlaceholder")}
                        onChange={(e) => {
                          e.preventDefault();
                          setNumberSearchQuery(e.target.value);
                        }}
                      />
                      <ul>
                        {filteredPrefixes.map((activePrefix, index) => {
                          return (
                            <li key={index}>
                              <button
                                className={styles.prefixListButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActivePrefix(activePrefix.dial_code);
                                  setIsPhonePrefixOpen(false);
                                  setNumberSearchQuery("");
                                }}
                              >
                                <span>
                                  <CircleFlag
                                    countryCode={activePrefix.code.toLowerCase()}
                                    className={styles.countryIcon}
                                  />
                                </span>
                                <span>{activePrefix.dial_code}</span>
                                <span className={styles.phoneCountryText}>
                                  {displayNames.of(activePrefix.code) ?? ""}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <button type="submit" className={styles.sendButton}>
                {t("send")}
              </button>
            </form>
          ) : (
            <div className={styles.contactForm}>
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  const tourDate =
                    userType === UserTypes.PRIVATEUSER
                      ? selectedDate
                      : selectedSlot;
                  if (!tourDate) {
                    toast(t("invalidTourDate"), { type: "error" });
                    return;
                  }
                  const validation = tourContactSchemaAuth.safeParse({
                    tourDate: tourDate,
                    listingId,
                  });
                  if (!validation.success) {
                    const firstError = validation.error.errors[0]?.message;
                    toast(t(firstError), { type: "error" });
                    return;
                  }
                  const isSuccess = await tourContactHandler(
                    userSub,
                    userType,
                    fullName,
                    email,
                    `${activePrefix + phoneNumberInput}`,
                    message,
                    listingId,
                    listingTitle,
                    userData.idToken,
                    userData.sub,
                    tourDate,
                  );
                  if (isSuccess) {
                    closeCallback();
                  }
                }}
                className={styles.sendButton}
              >
                {t("send")}
              </button>
            </div>
          )}
        </div>
        {phoneNumber !== null && <span className={styles.or}>{t("or")}</span>}
        {phoneNumber !== null && (
          <div className={styles.buttonsWrapper}>
            {!isPhoneNumberActive && (
              <button
                className={styles.button}
                onClick={(e) => {
                  e.preventDefault();
                  setIsPhoneNumberActive(true);
                }}
              >
                <span>
                  <FaPhoneAlt
                    className={`${styles.buttonIcon} ${styles.phoneIcon}`}
                  />
                </span>
                {t("showNumber")}
              </button>
            )}
            {phoneNumber !== null && isPhoneNumberActive && (
              <button
                className={styles.button}
                onClick={async (e) => {
                  e.preventDefault();

                  // 1. Ensure the value is a string, otherwise writeText crashes
                  const textToCopy = phoneNumber ? String(phoneNumber) : "";

                  if (!textToCopy) {
                    toast(t("copyFailed"), { type: "error" });
                    return;
                  }

                  // 2. Try the modern Clipboard API first (Requires HTTPS or Localhost)
                  if (navigator.clipboard && window.isSecureContext) {
                    try {
                      await navigator.clipboard.writeText(textToCopy);
                      toast(t("phoneCopied"), { type: "success" });
                      return; // Exit if successful
                    } catch (error) {
                      console.warn(
                        "Modern clipboard API failed, trying fallback...",
                        error,
                      );
                    }
                  }

                  // 3. The Bulletproof Fallback (Works on HTTP, older devices, and iOS WebViews)
                  try {
                    const textArea = document.createElement("textarea");
                    textArea.value = textToCopy;

                    // Hide the textarea entirely off-screen
                    textArea.style.position = "absolute";
                    textArea.style.left = "-999999px";

                    document.body.appendChild(textArea);

                    // Special selection logic to make iOS Safari happy
                    if (navigator.userAgent.match(/ipad|iphone/i)) {
                      const range = document.createRange();
                      range.selectNodeContents(textArea);
                      const selection = window.getSelection();
                      selection?.removeAllRanges();
                      selection?.addRange(range);
                      textArea.setSelectionRange(0, 999999);
                    } else {
                      textArea.select();
                    }

                    document.execCommand("copy");
                    textArea.remove();

                    toast(t("phoneCopied"), { type: "success" });
                  } catch (fallbackError) {
                    console.error(
                      "Fallback clipboard also failed:",
                      fallbackError,
                    );
                    toast(t("copyFailed"), { type: "error" });
                  }
                }}
                aria-label={t("copyPhoneNumber")}
              >
                <span className={styles.phoneText}>
                  <FaPhoneAlt
                    className={`${styles.buttonIcon} ${styles.phoneIcon}`}
                  />
                  <span>&lrm;{phoneNumber}</span>
                </span>
              </button>
            )}
            {hasWhatsAppActive && phoneNumber !== null && (
              <a
                className={`${styles.button} ${styles.whatsAppButton}`}
                target="_blank"
                rel="noopener noreferrer"
                href={`https://wa.me/${formatPhoneForWhatsApp(phoneNumber)}`}
              >
                <span>
                  <FaWhatsapp className={styles.buttonIcon} />
                </span>
                {t("whatsApp")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatPhoneForWhatsApp(phone: string): string {
  return phone.replace(/[^0-9]/g, ""); // Keep only digits
}

function getNextDaysUTC(count: number): string[] {
  const now = new Date();
  const dates: string[] = [];

  for (let i = 1; i <= count; i++) {
    const d = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + i,
        0,
        0,
        0,
        0,
      ),
    );

    dates.push(d.toISOString());
  }

  return dates;
}
